import type { ReactNode } from 'react';
import { Button } from './Button';

export interface EmptyStateProps {
  icon: ReactNode;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  variant?: 'default' | 'filtered';
  /**
   * Compact in-table layout for "no filter match" rows: tighter padding,
   * a small muted icon (no gradient badge), a single muted line, and an
   * optional brand-colored clear link rendered from `action`.
   * Defaults to the full-size layout — existing callers are unaffected.
   */
  dense?: boolean;
  /**
   * Calm-v2 styling: monochrome icon in a flat surfaceSoft square (no
   * gradient badge), ink/muted text, calm action button. Applies to both
   * the full-size and `dense` layouts. Only valid in apps that import
   * `@elka/theme` v2/calm.css. Default (false) keeps legacy styling.
   */
  calm?: boolean;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  variant = 'default',
  dense = false,
  calm = false,
}: EmptyStateProps) {
  if (calm) {
    if (dense) {
      return (
        <div className="px-6 py-12 text-center">
          <div className="text-sub mx-auto mb-3 flex h-10 w-10 items-center justify-center">
            {icon}
          </div>
          <p className="text-muted text-sm">{title}</p>
          {description && <p className="text-sub mt-1 text-sm">{description}</p>}
          {action && (
            <button
              onClick={action.onClick}
              className="text-brand hover:text-brand-ink mt-3 text-sm font-semibold transition-colors"
            >
              {action.label}
            </button>
          )}
        </div>
      );
    }
    return (
      <div className="px-6 py-14 text-center">
        <div className="rounded-card bg-surface-soft border-line text-muted mx-auto mb-5 flex h-14 w-14 items-center justify-center border">
          {icon}
        </div>
        <h3 className="text-ink mb-1.5 text-base font-bold text-balance">{title}</h3>
        {description && (
          <p className="text-muted mx-auto mb-6 max-w-xs text-sm leading-relaxed">{description}</p>
        )}
        {action && (
          <Button onClick={action.onClick} calm variant="primary">
            {action.label}
          </Button>
        )}
      </div>
    );
  }

  if (dense) {
    return (
      <div className="animate-fadeIn px-6 py-12 text-center">
        <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center text-gray-300">
          {icon}
        </div>
        <p className="text-sm text-gray-500">{title}</p>
        {description && <p className="mt-1 text-sm text-gray-400">{description}</p>}
        {action && (
          <button
            onClick={action.onClick}
            className="mt-3 text-sm font-medium text-[#3C5FA8] transition-colors hover:text-[#0C1A55]"
          >
            {action.label}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="animate-fadeIn px-6 py-14 text-center">
      <div
        className={`mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl ${
          variant === 'filtered'
            ? 'bg-gradient-to-br from-orange-500 to-amber-600 shadow-lg shadow-orange-200/40'
            : 'bg-gradient-to-br from-[#0C1A55] via-[#3C5FA8] to-[#0C1A55] shadow-lg shadow-[#3C5FA8]/20'
        }`}
      >
        {icon}
      </div>
      <h3 className="mb-1.5 text-lg font-bold text-balance text-[#0C1A55]">{title}</h3>
      {description && (
        <p className="mx-auto mb-6 max-w-xs text-sm leading-relaxed text-gray-400">{description}</p>
      )}
      {action && (
        <Button onClick={action.onClick} variant="gradient">
          {action.label}
        </Button>
      )}
    </div>
  );
}
